import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DollarSignIcon, TrendingUpIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { Opportunity, OpportunityStage } from '../../types';
import { formatCurrency, getStageName, getStageColor } from '../../utils/helpers';
import { exportOpportunitiesToExcel } from '../../utils/excelExport';
export function Opportunities() {
  const {
    opportunities,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    contacts,
    companies
  } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactId: '',
    companyId: '',
    company: '',
    industry: '',
    size: '',
    location: '',
    stage: 'contacto_inicial' as OpportunityStage,
    amount: 0,
    probability: 0,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const filteredOpportunities = opportunities.filter(o => {
    // Resolve company name from ID if available, otherwise use legacy string
    const companyName = o.companyId ? companies.find(c => c.id === o.companyId)?.name || o.company : o.company;
    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase()) || companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = !filterStage || o.stage === filterStage;
    return matchesSearch && matchesStage;
  });
  const stageOptions = [{
    value: 'contacto_inicial',
    label: 'Contacto Inicial'
  }, {
    value: 'evaluacion',
    label: 'Evaluación'
  }, {
    value: 'procedimiento_programado',
    label: 'Procedimiento Programado'
  }, {
    value: 'completado',
    label: 'Completado'
  }, {
    value: 'facturacion',
    label: 'Facturación'
  }];
  const contactOptions = contacts.map(c => ({
    value: c.id,
    label: c.name
  }));
  const companyOptions = companies.map(c => ({
    value: c.id,
    label: c.name
  }));
  const openNewForm = () => {
    setSelectedOpportunity(null);
    setFormData({
      name: '',
      contactId: '',
      companyId: '',
      company: '',
      industry: '',
      size: '',
      location: '',
      stage: 'contacto_inicial',
      amount: 0,
      probability: 0,
      notes: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setFormData({
      name: opportunity.name,
      contactId: opportunity.contactId || '',
      companyId: opportunity.companyId || '',
      company: opportunity.company,
      industry: opportunity.industry,
      size: opportunity.size,
      location: opportunity.location,
      stage: opportunity.stage,
      amount: opportunity.amount,
      probability: opportunity.probability,
      notes: opportunity.notes
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDeleteConfirmOpen(true);
  };
  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    if (selectedCompany) {
      setFormData({
        ...formData,
        companyId: companyId,
        company: selectedCompany.name,
        industry: selectedCompany.industry,
        size: selectedCompany.size,
        location: selectedCompany.location
      });
    } else {
      setFormData({
        ...formData,
        companyId: companyId
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    // Require companyId if companies exist, otherwise allow legacy string
    if (companies.length > 0 && !formData.companyId) {
      errors.company = 'La empresa es requerida';
    } else if (!formData.company && !formData.companyId) {
      errors.company = 'La empresa es requerida';
    }
    if (formData.amount < 0) errors.amount = 'El monto no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const opportunityData = {
      ...formData,
      contactId: formData.contactId || null,
      companyId: formData.companyId || null
    };
    if (selectedOpportunity) {
      updateOpportunity(selectedOpportunity.id, opportunityData);
    } else {
      addOpportunity(opportunityData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (selectedOpportunity) {
      deleteOpportunity(selectedOpportunity.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportOpportunitiesToExcel(opportunities);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-1032">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1033">
        <div data-id="element-1034">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1035">
            Oportunidades
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1036">
            Gestión de oportunidades comerciales B2B
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1038" />} onClick={openNewForm} data-id="element-1037">
          Nueva Oportunidad
        </Button>
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800" data-id="element-1039">
        <div className="flex items-center justify-between" data-id="element-1040">
          <div className="flex items-center gap-4" data-id="element-1041">
            <div className="w-14 h-14 rounded-xl bg-emerald-500 dark:bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20" data-id="element-1042">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1043" />
            </div>
            <div data-id="element-1044">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1045">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1046">
                Descarga todas las oportunidades en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-1047">
                <div className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400" data-id="element-1048">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-1049" />
                  <span className="font-medium" data-id="element-1050">
                    {opportunities.length} oportunidades
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1051">
                  • Incluye: Nombre, Empresa, Etapa, Monto, Probabilidad
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1053" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-200" data-id="element-1052">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Filters */}
      <Card data-id="element-1054">
        <div className="flex items-center gap-4" data-id="element-1055">
          <div className="flex-1 relative" data-id="element-1056">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1057" />
            <Input placeholder="Buscar oportunidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1058" />
          </div>
          <div className="w-64" data-id="element-1059">
            <Select options={[{
            value: '',
            label: 'Todas las etapas'
          }, ...stageOptions]} value={filterStage} onChange={setFilterStage} placeholder="Filtrar por etapa" data-id="element-1060" />
          </div>
        </div>
      </Card>

      {/* Opportunities List */}
      <Card padding="none" data-id="element-1061">
        <div className="overflow-x-auto" data-id="element-1062">
          <table className="w-full" data-id="element-1063">
            <thead data-id="element-1064">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1065">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1066">
                  Oportunidad
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1067">
                  Empresa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1068">
                  Etapa
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1069">
                  Monto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1070">
                  Probabilidad
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1071">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1072">
              {filteredOpportunities.length === 0 ? <tr data-id="element-1073">
                  <td colSpan={6} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1074">
                    No se encontraron oportunidades
                  </td>
                </tr> : filteredOpportunities.map(opp => {
              const companyName = opp.companyId ? companies.find(c => c.id === opp.companyId)?.name || opp.company : opp.company;
              return <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1075">
                      <td className="py-4 px-6" data-id="element-1076">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-1077">
                          {opp.name}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-1078">
                        <p className="text-gray-900 dark:text-gray-100" data-id="element-1079">
                          {companyName}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1080">
                          {opp.industry}
                        </p>
                      </td>
                      <td className="py-4 px-6" data-id="element-1081">
                        <div className="flex items-center gap-2" data-id="element-1082">
                          <div className={`w-2 h-2 rounded-full ${getStageColor(opp.stage)}`} data-id="element-1083" />
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-1084">
                            {getStageName(opp.stage)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900 dark:text-gray-100" data-id="element-1085">
                        {formatCurrency(opp.amount)}
                      </td>
                      <td className="py-4 px-6" data-id="element-1086">
                        <div className="flex items-center gap-2" data-id="element-1087">
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-1088">
                            <div className="h-full bg-sky-500" style={{
                        width: `${opp.probability}%`
                      }} data-id="element-1089" />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1090">
                            {opp.probability}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-1091">
                        <div className="flex items-center justify-end gap-2" data-id="element-1092">
                          <button onClick={() => openEditForm(opp)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" data-id="element-1093">
                            <EditIcon className="w-4 h-4" data-id="element-1094" />
                          </button>
                          <button onClick={() => openDeleteConfirm(opp)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-1095">
                            <TrashIcon className="w-4 h-4" data-id="element-1096" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedOpportunity ? 'Editar Oportunidad' : 'Nueva Oportunidad'} size="lg" data-id="element-1097">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1098">
          <Input label="Nombre de la Oportunidad" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} data-id="element-1099" />

          <div className="grid grid-cols-2 gap-4" data-id="element-1100">
            <Select label="Empresa" options={companyOptions} value={formData.companyId} onChange={handleCompanyChange} searchable error={formErrors.company} data-id="element-1101" />
            <Select label="Contacto Principal" options={contactOptions} value={formData.contactId} onChange={value => setFormData({
            ...formData,
            contactId: value
          })} searchable data-id="element-1102" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1103">
            <Input label="Industria" value={formData.industry} onChange={e => setFormData({
            ...formData,
            industry: e.target.value
          })} data-id="element-1104" />
            <Input label="Tamaño de Empresa" value={formData.size} onChange={e => setFormData({
            ...formData,
            size: e.target.value
          })} placeholder="Ej: 50-100 empleados" data-id="element-1105" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1106">
            <Select label="Etapa" options={stageOptions} value={formData.stage} onChange={value => setFormData({
            ...formData,
            stage: value as OpportunityStage
          })} data-id="element-1107" />
            <Input label="Ubicación" value={formData.location} onChange={e => setFormData({
            ...formData,
            location: e.target.value
          })} data-id="element-1108" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1109">
            <Input type="number" label="Monto Estimado (DOP)" value={formData.amount} onChange={e => setFormData({
            ...formData,
            amount: parseFloat(e.target.value) || 0
          })} error={formErrors.amount} min={0} data-id="element-1110" />
            <div className="space-y-1" data-id="element-1111">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1112">
                Probabilidad ({formData.probability}%)
              </label>
              <input type="range" min="0" max="100" step="5" value={formData.probability} onChange={e => setFormData({
              ...formData,
              probability: parseInt(e.target.value)
            })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700" data-id="element-1113" />
            </div>
          </div>

          <Textarea label="Notas" value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={3} data-id="element-1114" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-1115">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1116">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1117">
              {selectedOpportunity ? 'Guardar Cambios' : 'Crear Oportunidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1118">
        <div className="space-y-4" data-id="element-1119">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1120">
            ¿Está seguro de que desea eliminar la oportunidad "
            {selectedOpportunity?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-1121">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1122">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1123">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}